import ApplicationController from '../../javascript/controllers/application_controller'
import { readUrlClickIds, storeClickIds } from '../../javascript/lib/ad_attribution'
import {
  marketingConsentDecision,
  decisionIsCurrent,
  recordMarketingConsent,
  POLICY_VERSION,
} from '../../javascript/lib/marketing_consent'

// Reveals the consent box only for ad-referred visitors with no current decision.
// Click IDs are held on THIS element's dataset (a DOM attribute survives the
// data-turbo-permanent transplant; the Stimulus instance does NOT), written to
// sessionStorage only on grant (§ 25 TDDDG: no pre-consent device write).
export default class extends ApplicationController {
  static targets = ['box']

  connect() {
    try {
      this.hydrateHeld()
      if (this.hasClickId() && !this.alreadyDecided()) {
        this.reveal()
        this.addBreadcrumb('Consent', 'marketing_prompt_shown', { version: POLICY_VERSION })
      } else {
        this.hide()
      }
    } catch (error) {
      this.reportError(error, { source: 'ad_attribution_consent_banner' })
    }
  }

  grant() {
    try {
      if (!recordMarketingConsent(true)) {
        this.reportError(new Error('marketing consent write failed'), {
          source: 'ad_attribution_consent_banner',
          action: 'grant',
        })
        return // cannot confirm persistence — do not store, dispatch, or hide
      }
      storeClickIds(this.readHeld())
      document.dispatchEvent(new CustomEvent('marketing-consent:granted'))
      this.addBreadcrumb('Consent', 'marketing_granted', { version: POLICY_VERSION })
      this.hide()
    } catch (error) {
      this.reportError(error, { source: 'ad_attribution_consent_banner', action: 'grant' })
      // banner stays visible so the user can retry
    }
  }

  decline() {
    if (!recordMarketingConsent(false)) {
      this.reportError(new Error('marketing consent write failed'), {
        source: 'ad_attribution_consent_banner',
        action: 'decline',
      })
      return // denial not persisted — keep the banner visible to retry
    }
    this.addBreadcrumb('Consent', 'marketing_denied', { version: POLICY_VERSION })
    this.hide()
  }

  // Merge URL click IDs into the held set on this element's dataset (survives Turbo nav).
  hydrateHeld() {
    const held = this.readHeld()
    const urlIds = readUrlClickIds()
    let changed = false
    for (const key of Object.keys(urlIds)) {
      if (held[key] !== urlIds[key]) {
        held[key] = urlIds[key]
        changed = true
      }
    }
    if (changed) this.element.dataset.adClickIds = JSON.stringify(held)
  }

  readHeld() {
    try {
      return JSON.parse(this.element.dataset.adClickIds || '{}')
    } catch {
      return {}
    }
  }

  hasClickId() {
    return Object.keys(this.readHeld()).length > 0
  }

  // A current local decision. Same-origin now, so a grant recorded on marketing is
  // read here directly — this keeps the register box hidden for marketing-first
  // consenters even though the banner connects before the capture controller.
  alreadyDecided() {
    return marketingConsentDecision() !== null && decisionIsCurrent()
  }

  reveal() {
    if (this.hasBoxTarget) this.boxTarget.classList.remove('hidden')
  }

  hide() {
    if (this.hasBoxTarget) this.boxTarget.classList.add('hidden')
  }
}
